import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { TextArea } from '@progress/kendo-react-inputs';
import { checkIcon, copyIcon, filePdfIcon, fileTxtIcon, shareIcon, thumbDownIcon, thumbDownOutlineIcon, thumbUpIcon, thumbUpOutlineIcon } from '@progress/kendo-svg-icons';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import React from 'react';
import { useSelector } from 'react-redux';
import { API_ENDPOINTS, streamAgentMessageUrl } from '../../config/Config';
import { Resources } from '../../config/CommonResources';
import { RootState } from '../../store/reducer/rootReducer';
import { AgentLiveSteps, AgentTrace, IAgentTraceStep } from './AgentTrace';
import { exportChatAsMarkdown, exportChatAsPdf, IExportChat } from './agentChatExport';

interface IAgentCardAction {
  title: string;
  // The button's submit payload, sent back unchanged when the button is clicked.
  valueJson: string;
}

interface IAgentReply {
  conversationId: string;
  text: string;
  citations: string[];
  connectUrl: string | null;
  actions: IAgentCardAction[];
}

// One line of the streamed response (newline-delimited JSON): progress while the agent works, then the reply.
interface IAgentStreamEvent {
  type: 'step' | 'reply' | 'error';
  step?: IAgentTraceStep;
  reply?: IAgentReply;
  message?: string;
}

interface IAgentSummary {
  key: string;
  displayName: string;
  description: string;
  iconUrl: string | null;
}

interface IAgentDefinition {
  id: string;
  name: string;
  description: string;
  iconUrl: string | null;
}

interface IAgentChatMessage {
  id: number;
  role: 'user' | 'agent';
  text: string;
  // Only set on real (non-error) agent replies.
  responseTimeSeconds?: number;
  feedback?: 'up' | 'down' | null;
  // Set when the agent needs a connector authorized before it can answer - link to open, then retry.
  connectUrl?: string;
  // Card buttons (e.g. "Allow" / "Cancel" on a connector consent card) - the agent waits for one to be clicked.
  actions?: IAgentCardAction[];
  actionsUsed?: boolean;
  // The tool calls the agent made to produce this reply (dataset search, queries, ...), for transparency.
  trace?: IAgentTraceStep[];
}

interface IAgentChatSession {
  id: number;
  agentId: string;
  title: string;
  messages: IAgentChatMessage[];
  // The agent's conversation id, to continue the same conversation on the next turn.
  conversationId?: string;
}

const RAIL_STORAGE_KEY = 'atlas-agents-rail';
const ORB_GRADIENTS = [
  'linear-gradient(135deg,#2d5bff,#6a8bff)',
  'linear-gradient(135deg,#8b5cf6,#b79bff)',
  'linear-gradient(135deg,#12b886,#4fd6ac)',
  'linear-gradient(135deg,#ff7a59,#ffa98f)',
];

const isNarrowScreen = (): boolean => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

const readRailCollapsed = (): boolean => {
  if (isNarrowScreen()) return true;
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === 'collapsed';
  } catch {
    return false;
  }
};

const SparkleIcon = ({ size = 17 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
  </svg>
);

const AgentOrb = ({ agent, index }: { agent: IAgentDefinition | null; index: number }) => (
  <span
    className={`agent-orb${agent?.iconUrl ? ' has-icon' : ''}`}
    style={agent?.iconUrl ? undefined : { background: ORB_GRADIENTS[index % ORB_GRADIENTS.length] }}
  >
    {agent?.iconUrl ? <img src={agent.iconUrl} alt="" /> : <SparkleIcon size={15} />}
  </span>
);

let nextChatId = 1;
let nextMessageId = 1000;

marked.setOptions({ breaks: true });

function renderAgentMessageHtml(text: string): { __html: string } {
  const html = marked.parse(text, { async: false }) as string;
  return { __html: DOMPurify.sanitize(html) };
}

// navigator.clipboard only exists in secure contexts (https / localhost) and can be blocked by permissions
// policy, so fall back to a hidden textarea + execCommand rather than silently copying nothing.
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
  }
}

function AgentsView(): React.ReactElement {
  const [agents, setAgents] = React.useState<IAgentDefinition[]>([]);
  const [loadingAgents, setLoadingAgents] = React.useState<boolean>(true);
  const [agentsError, setAgentsError] = React.useState<boolean>(false);
  const [chats, setChats] = React.useState<IAgentChatSession[]>([]);
  const [selectedAgentId, setSelectedAgentId] = React.useState<string>('');
  const [activeChatId, setActiveChatId] = React.useState<number | null>(null);
  const [draft, setDraft] = React.useState<string>('');
  const [sending, setSending] = React.useState<boolean>(false);
  const [sendingChatId, setSendingChatId] = React.useState<number | null>(null);
  const [thinkingSeconds, setThinkingSeconds] = React.useState<number>(0);
  // Tool calls reported so far for the turn that is currently in flight.
  const [liveSteps, setLiveSteps] = React.useState<IAgentTraceStep[]>([]);
  const [feedbackMenuMessageId, setFeedbackMenuMessageId] = React.useState<number | null>(null);
  const [copiedMessageId, setCopiedMessageId] = React.useState<number | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = React.useState<boolean>(false);
  const [exporting, setExporting] = React.useState<boolean>(false);
  const [exportFailed, setExportFailed] = React.useState<boolean>(false);
  const [railCollapsed, setRailCollapsed] = React.useState<boolean>(readRailCollapsed);
  const firstName = useSelector((state: RootState) => state.security.userInfo?.givenName);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const feedbackMenuRef = React.useRef<HTMLDivElement>(null);
  const exportMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get<IAgentSummary[]>(API_ENDPOINTS.GET_AGENTS);
        if (cancelled) return;
        const mapped: IAgentDefinition[] = (data ?? []).map((a) => ({ id: a.key, name: a.displayName, description: a.description, iconUrl: a.iconUrl }));
        setAgents(mapped);
        if (mapped.length > 0) {
          setSelectedAgentId(mapped[0].id);
          const newChat: IAgentChatSession = { id: nextChatId++, agentId: mapped[0].id, title: Resources.Agents_NewChat, messages: [] };
          setChats([newChat]);
          setActiveChatId(newChat.id);
        }
      } catch (err) {
        if (!cancelled) setAgentsError(true);
      } finally {
        if (!cancelled) setLoadingAgents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (feedbackMenuMessageId == null) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (feedbackMenuRef.current && !feedbackMenuRef.current.contains(e.target as Node)) {
        setFeedbackMenuMessageId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [feedbackMenuMessageId]);

  React.useEffect(() => {
    if (!exportMenuOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [exportMenuOpen]);

  const currentAgent = agents.find((a) => a.id === selectedAgentId) ?? null;
  const activeChat = chats.find((c) => c.id === activeChatId);
  const isThinkingHere = sending && sendingChatId === activeChatId;

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChat?.messages.length, isThinkingHere]);

  React.useEffect(() => {
    if (!sending) {
      setThinkingSeconds(0);
      return;
    }
    setThinkingSeconds(0);
    const intervalId = setInterval(() => setThinkingSeconds((prev) => prev + 1), 1000);
    return () => clearInterval(intervalId);
  }, [sending]);

  const toggleRail = () => {
    const next = !railCollapsed;
    setRailCollapsed(next);
    try {
      localStorage.setItem(RAIL_STORAGE_KEY, next ? 'collapsed' : 'open');
    } catch {
      /* storage unavailable - the preference just isn't remembered */
    }
  };

  // On narrow screens the expanded rail overlays the chat, so close it once a choice is made.
  const closeRailIfNarrow = () => {
    if (isNarrowScreen()) setRailCollapsed(true);
  };

  const handleNewChat = () => {
    if (!selectedAgentId) return;
    closeRailIfNarrow();
    const newChat: IAgentChatSession = {
      id: nextChatId++,
      agentId: selectedAgentId,
      title: Resources.Agents_NewChat,
      messages: [],
    };
    setChats((prev) => [newChat, ...prev]);
    setActiveChatId(newChat.id);
  };

  const handleSelectAgent = (agentId: string) => {
    closeRailIfNarrow();
    setSelectedAgentId(agentId);
    const mostRecentChat = chats.find((c) => c.agentId === agentId);
    if (mostRecentChat) {
      setActiveChatId(mostRecentChat.id);
    } else {
      const newChat: IAgentChatSession = { id: nextChatId++, agentId, title: Resources.Agents_NewChat, messages: [] };
      setChats((prev) => [newChat, ...prev]);
      setActiveChatId(newChat.id);
    }
  };

  const handleSelectChat = (chat: IAgentChatSession) => {
    closeRailIfNarrow();
    setActiveChatId(chat.id);
    setSelectedAgentId(chat.agentId);
  };

  const sendToAgent = async (text: string, valueJson?: string) => {
    if (!text || !activeChat || !currentAgent || sending) return;
    const agent = currentAgent;
    const userMessage: IAgentChatMessage = { id: nextMessageId++, role: 'user', text };
    const chatId = activeChat.id;
    setChats((prev) =>
      prev.map((c) =>
        c.id === chatId
          ? { ...c, title: c.messages.length === 0 ? text.slice(0, 60) : c.title, messages: [...c.messages, userMessage] }
          : c,
      ),
    );

    setSending(true);
    setSendingChatId(chatId);
    setLiveSteps([]);
    const requestStartedAt = Date.now();

    // The response is newline-delimited JSON events. Tool-call steps arrive while the agent works (read
    // here as the response body grows); the final reply is the last line.
    const steps: IAgentTraceStep[] = [];
    // Held in an object because the reads below happen after callbacks assign it (TypeScript won't track closure writes to a `let`).
    const outcome: { reply?: IAgentReply; error?: string } = {};
    let processed = 0;
    const consume = (body: string | undefined) => {
      if (!body) return;
      const end = body.lastIndexOf('\n');
      if (end < processed) return; // no complete new line yet
      const lines = body.slice(processed, end + 1).split('\n');
      processed = end + 1;
      let stepsChanged = false;
      for (const line of lines) {
        if (!line.trim()) continue;
        let streamEvent: IAgentStreamEvent;
        try {
          streamEvent = JSON.parse(line);
        } catch {
          continue;
        }
        const incomingStep = streamEvent.step;
        if (streamEvent.type === 'step' && incomingStep) {
          const index = steps.findIndex((s) => s.id === incomingStep.id);
          if (index >= 0) steps[index] = incomingStep;
          else steps.push(incomingStep);
          stepsChanged = true;
        } else if (streamEvent.type === 'reply') {
          outcome.reply = streamEvent.reply;
        } else if (streamEvent.type === 'error') {
          outcome.error = streamEvent.message || 'error';
        }
      }
      if (stepsChanged) setLiveSteps([...steps]);
    };

    try {
      const response = await axios.post<string>(
        streamAgentMessageUrl(agent.id),
        {
          conversationId: activeChat.conversationId ?? null,
          text,
          valueJson: valueJson ?? null,
        },
        {
          responseType: 'text',
          transformResponse: [(raw) => raw],
          onDownloadProgress: (progress) => consume((progress.event?.target as XMLHttpRequest | undefined)?.responseText),
        },
      );
      consume(response.data);
      const data = outcome.reply;
      if (outcome.error && !data) throw new Error(outcome.error);
      const replyMessage: IAgentChatMessage = {
        id: nextMessageId++,
        role: 'agent',
        text: data?.text || `I didn't get a reply from ${agent.name}. Please try again.`,
        responseTimeSeconds: Math.round((Date.now() - requestStartedAt) / 1000),
        connectUrl: data?.connectUrl ?? undefined,
        actions: data?.actions?.length ? data.actions : undefined,
        trace: steps.length ? [...steps] : undefined,
      };
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId ? { ...c, conversationId: data?.conversationId ?? c.conversationId, messages: [...c.messages, replyMessage] } : c,
        ),
      );
    } catch (err) {
      const errorMessage: IAgentChatMessage = {
        id: nextMessageId++,
        role: 'agent',
        text: `${agent.name} is unavailable right now. Please try again in a moment.`,
      };
      setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, messages: [...c.messages, errorMessage] } : c)));
    } finally {
      setSending(false);
      setSendingChatId(null);
      setLiveSteps([]);
    }
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    sendToAgent(text);
  };

  const handleCardAction = (messageId: number, action: IAgentCardAction) => {
    if (!activeChat || sending) return;
    const chatId = activeChat.id;
    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, messages: c.messages.map((m) => (m.id === messageId ? { ...m, actionsUsed: true } : m)) } : c)),
    );
    sendToAgent(action.title, action.valueJson);
  };

  const handleCopyMessage = async (messageId: number, text: string) => {
    if (!(await copyTextToClipboard(text))) return;
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId((current) => (current === messageId ? null : current)), 1500);
  };

  const handleExport = async (format: 'pdf' | 'md') => {
    setExportMenuOpen(false);
    if (!activeChat || exporting) return;
    const chat: IExportChat = {
      title: activeChat.title,
      agentName: currentAgent?.name ?? 'Agent',
      messages: activeChat.messages.map((m) => ({ role: m.role, text: m.text, trace: m.trace })),
    };
    setExporting(true);
    setExportFailed(false);
    try {
      if (format === 'pdf') await exportChatAsPdf(chat);
      else exportChatAsMarkdown(chat);
    } catch (err) {
      console.error('Chat export failed', err);
      setExportFailed(true);
      setTimeout(() => setExportFailed(false), 4000);
    } finally {
      setExporting(false);
    }
  };

  const handleShareMessage = (text: string) => {
    if (navigator.share) {
      navigator.share({ text }).catch(() => {
        /* user cancelled the share sheet - nothing to do */
      });
    } else {
      navigator.clipboard?.writeText(text);
    }
  };

  const handleFeedback = (messageId: number, value: 'up' | 'down') => {
    if (!activeChat) return;
    const chatId = activeChat.id;
    setChats((prev) =>
      prev.map((c) =>
        c.id === chatId
          ? {
              ...c,
              messages: c.messages.map((m) => (m.id === messageId ? { ...m, feedback: m.feedback === value ? null : value } : m)),
            }
          : c,
      ),
    );
    setFeedbackMenuMessageId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const displayedMessages: IAgentChatMessage[] = activeChat?.messages ?? [];
  const currentAgentIndex = Math.max(
    0,
    agents.findIndex((a) => a.id === selectedAgentId),
  );
  const showWelcome = displayedMessages.length === 0 && !isThinkingHere;

  return (
    <div className={`agents-view${railCollapsed ? ' rail-collapsed' : ''}`}>
      <div className={`agents-rail${railCollapsed ? ' slim' : ''}`} role="navigation" aria-label="Agents">
        <button
          type="button"
          className="agents-new-chat-btn"
          title={Resources.Agents_NewChat}
          aria-label={Resources.Agents_NewChat}
          onClick={handleNewChat}
          disabled={!selectedAgentId}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="agents-rail-text">{Resources.Agents_NewChat}</span>
        </button>
        <div className="agents-rail-label">{Resources.Agents_SelectAgent}</div>
        {loadingAgents && agents.length === 0 && <div className="agents-rail-status">Loading agents…</div>}
        {agentsError && agents.length === 0 && !loadingAgents && <div className="agents-rail-status">Unable to load agents.</div>}
        {agents.map((agent, index) => (
          <button
            key={agent.id}
            type="button"
            title={agent.name}
            className={`agents-rail-agent${agent.id === selectedAgentId ? ' active' : ''}`}
            onClick={() => handleSelectAgent(agent.id)}
          >
            <AgentOrb agent={agent} index={index} />
            <span className="agents-rail-text">
              <b>{agent.name}</b>
              <small>{agent.description}</small>
            </span>
          </button>
        ))}
        <div className="agents-rail-label">{Resources.Agents_RecentChats}</div>
        {/* Empty chats stay out of the list (except the open one) so browsing agents doesn't pile up "New chat" entries. */}
        {chats
          .filter((chat) => chat.messages.length > 0 || chat.id === activeChatId)
          .map((chat) => {
            const chatAgentIndex = agents.findIndex((a) => a.id === chat.agentId);
            const chatAgent = agents[chatAgentIndex] ?? null;
            return (
              <button
                key={chat.id}
                type="button"
                title={chatAgent ? `${chat.title} · ${chatAgent.name}` : chat.title}
                className={`agents-rail-chat${chat.id === activeChatId ? ' active' : ''}`}
                onClick={() => handleSelectChat(chat)}
              >
                <AgentOrb agent={chatAgent} index={Math.max(0, chatAgentIndex)} />
                <span className="agents-rail-chat-text">
                  <b>{chat.title}</b>
                  {chatAgent && <small>{chatAgent.name}</small>}
                </span>
              </button>
            );
          })}
      </div>
      <div className="agents-chat-panel">
        <div className="agents-chat-header">
          <button type="button" className="agents-icon-btn" title={Resources.Agents_ToggleSidebar} aria-label={Resources.Agents_ToggleSidebar} onClick={toggleRail}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="3" />
              <path d="M9 4v16" />
            </svg>
          </button>
          {currentAgent && <AgentOrb agent={currentAgent} index={currentAgentIndex} />}
          <span className="agents-chat-header-title">{currentAgent?.name ?? ''}</span>
          <span className="agents-chat-header-desc">{currentAgent?.description ?? ''}</span>
          <div className="agents-export-wrapper" ref={exportMenuRef}>
            <button
              type="button"
              className="agents-export-btn"
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              title={Resources.Agents_Export}
              disabled={displayedMessages.length === 0 || exporting}
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
              </svg>
              <span className="agents-export-label">{exporting ? Resources.Agents_Exporting : exportFailed ? Resources.Agents_ExportFailed : Resources.Agents_Export}</span>
            </button>
            {exportMenuOpen && (
              <div className="agents-export-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => handleExport('pdf')}>
                  <SvgIcon icon={filePdfIcon} />
                  {Resources.Agents_Export_Pdf}
                </button>
                <button type="button" role="menuitem" onClick={() => handleExport('md')}>
                  <SvgIcon icon={fileTxtIcon} />
                  {Resources.Agents_Export_Markdown}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="agents-chat-messages">
          {showWelcome && (
            <div className="agents-welcome">
              <div className="agents-welcome-core">
                <span className="agents-welcome-ring" />
                <SparkleIcon size={24} />
              </div>
              <h3>
                {Resources.Agents_Greeting}
                {firstName ? (
                  <>
                    , <span className="agents-serif">{firstName}?</span>
                  </>
                ) : (
                  '?'
                )}
              </h3>
              <p>{currentAgent?.description ?? ''}</p>
            </div>
          )}
          {displayedMessages.map((message) => (
            <div key={message.id} className={`agents-chat-message-group ${message.role}`}>
              <div className={`agents-chat-bubble ${message.role}`}>
                {message.role === 'agent' ? (
                  <div className="agents-chat-bubble-markdown" dangerouslySetInnerHTML={renderAgentMessageHtml(message.text)} />
                ) : (
                  message.text
                )}
                {message.connectUrl && (
                  <div className="agents-chat-connect-link">
                    <a href={message.connectUrl} target="_blank" rel="noopener noreferrer">
                      Open connection manager
                    </a>
                  </div>
                )}
                {message.actions && (
                  <div className="agents-chat-card-actions">
                    {message.actions.map((action, index) => (
                      <Button
                        key={index}
                        themeColor={index === 0 ? 'primary' : 'base'}
                        disabled={message.actionsUsed || sending}
                        onClick={() => handleCardAction(message.id, action)}
                      >
                        {action.title}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
              {message.role === 'agent' && message.trace && <AgentTrace steps={message.trace} />}
              {message.role === 'agent' && message.id !== 0 && (
                <div className="agents-message-actions">
                  <button
                    type="button"
                    title={copiedMessageId === message.id ? 'Copied' : 'Copy'}
                    className={copiedMessageId === message.id ? 'active' : undefined}
                    onClick={() => handleCopyMessage(message.id, message.text)}
                  >
                    <SvgIcon icon={copiedMessageId === message.id ? checkIcon : copyIcon} />
                  </button>
                  <div className="agents-feedback-wrapper" ref={feedbackMenuMessageId === message.id ? feedbackMenuRef : undefined}>
                    {message.feedback ? (
                      <button
                        type="button"
                        title="Rate this response"
                        className="active"
                        onClick={() => setFeedbackMenuMessageId(feedbackMenuMessageId === message.id ? null : message.id)}
                      >
                        <SvgIcon icon={message.feedback === 'up' ? thumbUpIcon : thumbDownIcon} />
                      </button>
                    ) : (
                      <>
                        <button type="button" title="Good response" onClick={() => handleFeedback(message.id, 'up')}>
                          <SvgIcon icon={thumbUpOutlineIcon} />
                        </button>
                        <button type="button" title="Bad response" onClick={() => handleFeedback(message.id, 'down')}>
                          <SvgIcon icon={thumbDownOutlineIcon} />
                        </button>
                      </>
                    )}
                    {feedbackMenuMessageId === message.id && (
                      <div className="agents-feedback-menu">
                        <button type="button" onClick={() => handleFeedback(message.id, 'up')}>
                          <SvgIcon icon={thumbUpOutlineIcon} />
                          Good response
                        </button>
                        <button type="button" onClick={() => handleFeedback(message.id, 'down')}>
                          <SvgIcon icon={thumbDownOutlineIcon} />
                          Bad response
                        </button>
                      </div>
                    )}
                  </div>
                  <button type="button" title="Share" onClick={() => handleShareMessage(message.text)}>
                    <SvgIcon icon={shareIcon} />
                  </button>
                  {message.responseTimeSeconds != null && <span className="agents-message-time">{message.responseTimeSeconds}s</span>}
                </div>
              )}
            </div>
          ))}
          {isThinkingHere && <AgentLiveSteps steps={liveSteps} seconds={thinkingSeconds} />}
          <div ref={messagesEndRef} />
        </div>
        <div className="agents-chat-input">
          <div className="agents-composer">
            <TextArea
              className="agents-chat-textarea"
              rows={1}
              // Kendo's autoSize measures 0px when this view mounts hidden (display: none in Dashboard),
              // so only enable it once there's text - an empty draft keeps the natural auto height.
              autoSize={draft.length > 0}
              placeholder={currentAgent ? `Ask ${currentAgent.name}…` : Resources.Agents_InputPlaceholder}
              value={draft}
              onChange={(e) => setDraft(e.value)}
              onKeyDown={handleKeyDown}
              disabled={!currentAgent}
            />
            <button type="button" className="agents-send-btn" aria-label="Send" disabled={!draft.trim() || sending || !currentAgent} onClick={handleSend}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
          <div className="agents-fineprint">{Resources.Agents_Disclaimer}</div>
        </div>
      </div>
    </div>
  );
}

export default React.memo(AgentsView);
